import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Chip,
    IconButton, Stack, LinearProgress, Tab, Tabs,
    Divider, Tooltip as MuiTooltip
} from '@mui/material';
import {
    Refresh, Engineering,
    Timeline as ActivityIcon, Speed, Hub,
    Circle as StatusLight,
    TrendingUp, Bolt,
    Psychology, Opacity, CheckCircle,
    PeopleAlt, Cancel, WarningAmber, CheckCircleOutline,
    DoneAll, FilterAlt
} from '@mui/icons-material';
import {
    Area, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, ComposedChart,
    Line, PieChart, Pie, Cell, RadialBarChart, RadialBar, ReferenceArea, Legend
} from 'recharts';
import { format } from 'date-fns';
import { getSystemVitals, VitalsData } from '../services/vitalsService';

// --- CONSTANTS & THEME ---
const COLORS = {
    bg: "#F1F5F9",       // Slate-100
    surface: "#FFFFFF",
    header: "#0F172A",   // Slate-900
    teal: "#0D9488",
    indigo: "#6366f1",
    amber: "#F59E0B",
    orange: "#EA580C",
    red: "#E11D48",
    green: "#10B981",
    slate: "#64748B",
    darkSlate: "#334155",
    border: "#E2E8F0",
    chartGrid: "#F1F5F9",
    prediction: "#818CF8",
    cancelled: "#F43F5E",
    successLight: "#DCFCE7",
    errorLight: "#FEE2E2"
};

// --- TYPES ---
interface HistoryPoint {
    timeStr: string;
    timestamp: number;
    waiting: number;
    matched: number;
    cancelled: number;
    completed: number;
    supply: number;
    cpu: number;
    sockets: number;
    isPrediction?: boolean;
}

// --- UTILS ---
const formatCompactNumber = (number: number) => {
    return Intl.NumberFormat('en-US', {
        notation: "compact",
        maximumFractionDigits: 1
    }).format(number);
};

// Safe Percentage Calculation (Avoids NaN)
const calculatePercentage = (part: number, total: number) => {
    if (!total || total === 0) return 0;
    return ((part / total) * 100);
};

const calculateTrend = (current: number, history: HistoryPoint[], key: keyof HistoryPoint) => {
    if (history.length < 5) return "Wait...";
    const oldPoint = history[0];
    const oldValue = (oldPoint[key] as number) || 0;

    if (oldValue === 0) return "+0.0%";

    const diff = current - oldValue;
    const percent = (diff / oldValue) * 100;
    return `${percent > 0 ? '+' : ''}${percent.toFixed(1)}%`;
};

// --- PREDICTION ENGINE ---
const calculatePrediction = (data: HistoryPoint[]): HistoryPoint[] => {
    if (data.length < 5) return [];

    const recent = data.slice(-10);
    const velocity = recent.reduce((acc, curr, i, arr) => {
        if (i === 0) return 0;
        return acc + (curr.matched - arr[i - 1].matched);
    }, 0) / (recent.length - 1);

    const lastPoint = data[data.length - 1];
    const predictions: HistoryPoint[] = [];

    for (let i = 1; i <= 10; i++) {
        const nextTime = new Date(lastPoint.timestamp + (i * 3000));
        predictions.push({
            ...lastPoint,
            timeStr: format(nextTime, 'HH:mm:ss'),
            timestamp: nextTime.getTime(),
            matched: Math.max(0, Math.round(lastPoint.matched + (velocity * i))),
            waiting: Math.max(0, Math.round(lastPoint.waiting + (velocity * i * 0.5))),
            completed: lastPoint.completed,
            cancelled: lastPoint.cancelled,
            supply: lastPoint.supply,
            cpu: lastPoint.cpu,
            sockets: lastPoint.sockets,
            isPrediction: true
        });
    }
    return predictions;
};

const Home: React.FC = () => {
    const [vitals, setVitals] = useState<VitalsData | null>(null);
    const [history, setHistory] = useState<HistoryPoint[]>([]);
    const [activeTab, setActiveTab] = useState(0);
    const [isSystemDown, setIsSystemDown] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Filter State for Graph
    const [chartFilters, setChartFilters] = useState({
        waiting: true,
        matched: true,
        completed: false, // Default hidden to reduce noise
        cancelled: false
    });

    const graphData = useMemo(() => {
        const prediction = calculatePrediction(history);
        return [...history, ...prediction];
    }, [history]);

    const fetchTelemetry = useCallback(async () => {
        try {
            const res = await getSystemVitals();
            const data = res.data;

            if (data) {
                setVitals(data);
                setIsSystemDown(false);

                setHistory(prev => {
                    const waiting = data.workers?.rideQueue?.waiting || 0;
                    const matched = data.workers?.rideQueue?.active || 0;
                    const cancelled = data.workers?.rideQueue?.cancelled || 0;
                    const completed = data.workers?.rideQueue?.completed || 0;

                    const newPoint: HistoryPoint = {
                        timeStr: format(new Date(), 'HH:mm:ss'),
                        timestamp: Date.now(),
                        waiting,
                        matched,
                        cancelled,
                        completed,
                        supply: data.marketplace?.totalGlobalSupply || 0,
                        cpu: parseFloat(data.api?.cpuLoad || "0") * 100,
                        sockets: data.marketplace?.activeSocketConnections || 0,
                        isPrediction: false
                    };
                    return [...prev, newPoint].slice(-60);
                });
            }
        } catch (e) {
            setIsSystemDown(true);
        } finally {
            setIsInitialLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTelemetry();
        const timer = setInterval(fetchTelemetry, 3000);
        return () => clearInterval(timer);
    }, [fetchTelemetry]);

    if (isInitialLoading && !vitals) return <LoadingScreen />;

    // --- ANALYTICS ---
    const currentSockets = vitals?.marketplace?.activeSocketConnections || 0;
    const currentWaiting = vitals?.workers?.rideQueue?.waiting || 0;
    const currentActive = vitals?.workers?.rideQueue?.active || 0;
    const currentCompleted = vitals?.workers?.rideQueue?.completed || 0;
    const currentCancelled = history[history.length - 1]?.cancelled || 0;

    // Tip Analytics
    const pendingWithTip = (vitals?.marketplace as any)?.pendingWithTip || 0;
    const pendingWithoutTip = Math.max(0, currentWaiting - pendingWithTip);
    const noTipPercentage = calculatePercentage(pendingWithoutTip, currentWaiting).toFixed(0);

    // User Counts
    const totalRiders = (vitals?.marketplace as any)?.totalRiders || 0;
    const totalDrivers = (vitals?.marketplace as any)?.totalDrivers || 0;
    const currentSupply = vitals?.marketplace?.totalGlobalSupply || 0;
    const onlineRiders = (vitals?.marketplace as any)?.totalOnlineRiders || 0;

    const totalRequests = currentWaiting + currentActive;
    const lastHistory = history[history.length - 1];
    const cancelRate = calculatePercentage(currentCancelled, totalRequests + currentCompleted + currentCancelled).toFixed(1);

    const funnelData = [
        { name: 'Waiting', value: currentWaiting, fill: COLORS.amber },
        { name: 'Active', value: currentActive, fill: COLORS.indigo },
        { name: 'Completed', value: currentCompleted, fill: COLORS.green },
        { name: 'Cancelled', value: currentCancelled, fill: COLORS.cancelled },
    ];
    const activeFunnelData = funnelData.filter(d => d.value > 0);

    const ghostRate = vitals?.marketplace?.ghostRate || 0;
    const ghostData = [{ name: 'Integrity', value: 100 - ghostRate, fill: COLORS.green }];

    const toggleFilter = (key: keyof typeof chartFilters) => {
        setChartFilters(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            width: '100%',
            display: 'flex', flexDirection: 'column',
            bgcolor: COLORS.bg,
            fontFamily: 'Inter, sans-serif',
            pb: 4
        }}>

            {/* --- HEADER --- */}
            <Paper elevation={0} sx={{
                p: 2, m: 2, borderRadius: 3, border: `1px solid ${COLORS.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                bgcolor: COLORS.surface
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: 3, bgcolor: COLORS.header,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}>
                        <Hub sx={{ color: isSystemDown ? COLORS.red : COLORS.green, fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1 }}>ZAPPPIT COMMAND CENTER</Typography>
                        <Typography variant="caption" fontWeight={700} sx={{ color: COLORS.slate }}>
                            {vitals?.nodeId?.toUpperCase()} • {vitals?.telemetryMeta?.activeShards} SHARDS
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Box sx={{
                        px: 2, py: 0.8, borderRadius: 50,
                        bgcolor: isSystemDown ? COLORS.errorLight : COLORS.successLight,
                        border: `1px solid ${isSystemDown ? COLORS.red : COLORS.green}`,
                        display: 'flex', alignItems: 'center', gap: 1
                    }}>
                        {isSystemDown ? <WarningAmber sx={{ fontSize: 18, color: COLORS.red }} /> : <CheckCircleOutline sx={{ fontSize: 18, color: COLORS.green }} />}
                        <Typography variant="caption" fontWeight={800} sx={{ color: isSystemDown ? COLORS.red : COLORS.green }}>
                            {isSystemDown ? "CRITICAL" : "OPERATIONAL"}
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ height: 30, display: { xs: 'none', md: 'block' } }} />
                    <MiniMetric label="LIVE SOCKETS" value={formatCompactNumber(currentSockets)} icon={<PeopleAlt fontSize="inherit" />} />
                    <Divider orientation="vertical" flexItem sx={{ height: 30, display: { xs: 'none', md: 'block' } }} />
                    <MiniMetric label="CPU LOAD" value={`${vitals?.api?.cpuLoad}%`} color={parseFloat(vitals?.api?.cpuLoad || '0') > 80 ? COLORS.red : COLORS.slate} />
                    <IconButton onClick={fetchTelemetry} size="small" sx={{ border: `1px solid ${COLORS.border}` }}><Refresh fontSize="small" /></IconButton>
                </Box>
            </Paper>

            {/* --- CONTENT AREA --- */}
            <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* HEADER 1 */}
                <Typography variant="subtitle2" fontWeight={800} color="textSecondary" sx={{ ml: 1, mt: 1 }}>
                    LIVE RIDE METRICS
                </Typography>

                {/* ROW 1: RIDE LIFECYCLE */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <KpiCard
                            title="Waiting for Drivers"
                            value={currentWaiting}
                            subValue={`${pendingWithTip} w/Tip • ${pendingWithoutTip} No-Tip (${noTipPercentage}%)`}
                            trend={calculateTrend(currentWaiting, history, 'waiting')}
                            color={COLORS.amber}
                            icon={<Speed />}
                            isLive={!isSystemDown}
                            tooltip="Rides currently in the matching pool waiting for a driver."
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <KpiCard
                            title="Active Rides"
                            value={currentActive}
                            subValue="Matched & In-Progress"
                            trend={calculateTrend(currentActive, history, 'matched')}
                            color={COLORS.indigo}
                            icon={<TrendingUp />}
                            isLive={!isSystemDown}
                            tooltip="Rides where a driver has been assigned or trip is in progress."
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <KpiCard
                            title="Completed Rides"
                            value={formatCompactNumber(currentCompleted)}
                            subValue="Successfully Finished"
                            trend="UP"
                            color={COLORS.green}
                            icon={<DoneAll />}
                            isLive={!isSystemDown}
                            tooltip="Total successful rides delivered since system start."
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 200px' }}>
                        <KpiCard
                            title="Cancelled Rides"
                            value={formatCompactNumber(currentCancelled)}
                            subValue={`${cancelRate}% Fail Rate`}
                            trend={parseFloat(cancelRate) > 10 ? "CRITICAL" : "STABLE"}
                            color={COLORS.cancelled}
                            icon={<Cancel />}
                            isInverse={true}
                            isLive={!isSystemDown}
                            tooltip="Rides cancelled by rider or driver before completion."
                        />
                    </Box>
                </Box>

                {/* HEADER 2 */}
                <Typography variant="subtitle2" fontWeight={800} color="textSecondary" sx={{ ml: 1, mt: 1 }}>
                    FLEET & ASSETS
                </Typography>

                {/* ROW 2: ASSETS */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
                    <Box sx={{ flex: '1 1 300px' }}>
                        <KpiCard
                            title="Rider Base"
                            value={formatCompactNumber(totalRiders)}
                            subValue={`${formatCompactNumber(onlineRiders)} Riders Online`}
                            trend="STABLE"
                            color={COLORS.indigo}
                            icon={<PeopleAlt />}
                            isLive={!isSystemDown}
                            tooltip="Total registered riders vs currently active sessions."
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 300px' }}>
                        <KpiCard
                            title="Driver Fleet"
                            value={formatCompactNumber(totalDrivers)}
                            subValue={`${formatCompactNumber(currentSupply)} Drivers Online`}
                            trend={calculateTrend(currentSupply, history, 'supply')}
                            color={COLORS.teal}
                            icon={<Engineering />}
                            isLive={!isSystemDown}
                            tooltip="Total registered drivers vs currently online and available."
                        />
                    </Box>
                </Box>

                {/* ROW 3: MAIN CHART */}
                <Box sx={{ width: '100%', minHeight: 450 }}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%', border: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Box>
                                <Typography variant="h6" fontWeight={800}>Marketplace Velocity</Typography>
                                <Typography variant="caption" color="textSecondary" fontWeight={600}>Lifecycle Flow (Waiting → Matched)</Typography>
                            </Box>

                            {/* CHART FILTERS */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <FilterAlt sx={{ color: COLORS.slate, opacity: 0.5, mr: 1 }} />
                                <Chip
                                    label="Waiting"
                                    size="small"
                                    onClick={() => toggleFilter('waiting')}
                                    sx={{ bgcolor: chartFilters.waiting ? `${COLORS.amber}20` : 'transparent', color: chartFilters.waiting ? COLORS.amber : COLORS.slate, fontWeight: 700, border: `1px solid ${chartFilters.waiting ? COLORS.amber : COLORS.border}` }}
                                />
                                <Chip
                                    label="Active"
                                    size="small"
                                    onClick={() => toggleFilter('matched')}
                                    sx={{ bgcolor: chartFilters.matched ? `${COLORS.indigo}20` : 'transparent', color: chartFilters.matched ? COLORS.indigo : COLORS.slate, fontWeight: 700, border: `1px solid ${chartFilters.matched ? COLORS.indigo : COLORS.border}` }}
                                />
                                <Chip
                                    label="Completed"
                                    size="small"
                                    onClick={() => toggleFilter('completed')}
                                    sx={{ bgcolor: chartFilters.completed ? `${COLORS.green}20` : 'transparent', color: chartFilters.completed ? COLORS.green : COLORS.slate, fontWeight: 700, border: `1px solid ${chartFilters.completed ? COLORS.green : COLORS.border}` }}
                                />
                                <Chip
                                    label="Cancelled"
                                    size="small"
                                    onClick={() => toggleFilter('cancelled')}
                                    sx={{ bgcolor: chartFilters.cancelled ? `${COLORS.cancelled}20` : 'transparent', color: chartFilters.cancelled ? COLORS.cancelled : COLORS.slate, fontWeight: 700, border: `1px solid ${chartFilters.cancelled ? COLORS.cancelled : COLORS.border}` }}
                                />
                            </Box>

                            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 36 }}>
                                <Tab label="Ride Flow" sx={{ fontWeight: 700 }} />
                                <Tab label="Infra Stress" sx={{ fontWeight: 700 }} />
                            </Tabs>
                        </Box>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={graphData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorMatched" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.indigo} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={COLORS.indigo} stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorWaiting" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={COLORS.amber} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={COLORS.amber} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.chartGrid} />
                                    <XAxis dataKey="timeStr" tick={{fontSize: 10}} interval={10} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="left" tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                                    <YAxis yAxisId="right" orientation="right" hide />
                                    <Tooltip contentStyle={{ backgroundColor: '#0F172A', borderRadius: 8, border: 'none', color: '#fff', fontSize: 12 }} itemStyle={{ color: '#fff' }} />

                                    {activeTab === 0 ? (
                                        <>
                                            <ReferenceArea x1={graphData.find(d => d.isPrediction)?.timeStr} strokeOpacity={0.3} fill={COLORS.slate} fillOpacity={0.05} />
                                            {chartFilters.cancelled && <Area yAxisId="left" type="monotone" stackId="1" dataKey="cancelled" stroke="none" fill={COLORS.cancelled} fillOpacity={0.6} isAnimationActive={false} />}
                                            {chartFilters.waiting && <Area yAxisId="left" type="monotone" stackId="1" dataKey="waiting" stroke={COLORS.amber} fill="url(#colorWaiting)" isAnimationActive={false} />}
                                            {chartFilters.matched && <Area yAxisId="left" type="monotone" stackId="1" dataKey="matched" stroke={COLORS.indigo} fill="url(#colorMatched)" strokeWidth={2} isAnimationActive={false} />}
                                            {chartFilters.completed && <Line yAxisId="left" type="monotone" dataKey="completed" stroke={COLORS.green} strokeWidth={2} dot={false} />}
                                            <Line yAxisId="left" type="step" dataKey="supply" stroke={COLORS.teal} strokeWidth={2} dot={false} />
                                        </>
                                    ) : (
                                        <>
                                            <Line yAxisId="left" type="monotone" dataKey="sockets" stroke={COLORS.indigo} strokeWidth={2} dot={false} />
                                            <Line yAxisId="right" type="monotone" dataKey="cpu" stroke={COLORS.orange} strokeWidth={2} dot={false} />
                                        </>
                                    )}
                                    <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', fontWeight: 600 }} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Box>

                {/* ROW 4: DISTRIBUTION & INTEGRITY */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>

                    {/* PIE CHART */}
                    <Box sx={{ flex: '1 1 300px' }}>
                        <Paper sx={{ p: 2, borderRadius: 3, border: `1px solid ${COLORS.border}`, height: '100%', minHeight: 350, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" fontWeight={800} color={COLORS.darkSlate}>CURRENT DISTRIBUTION</Typography>
                            <Box sx={{ flex: 1, position: 'relative' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={activeFunnelData.length > 0 ? activeFunnelData : [{name: 'Empty', value: 1}]}
                                            cx="50%" cy="50%"
                                            innerRadius={60} outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {activeFunnelData.length > 0 ? activeFunnelData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            )) : <Cell fill={COLORS.border} />}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '11px', fontWeight: 600 }}/>
                                    </PieChart>
                                </ResponsiveContainer>
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -60%)', textAlign: 'center' }}>
                                    <Typography variant="h4" fontWeight={900} color={COLORS.header}>
                                        {formatCompactNumber(currentActive)}
                                    </Typography>
                                    <Typography variant="caption" fontWeight={700} color="textSecondary">ACTIVE</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>

                    {/* INTEGRITY */}
                    <Box sx={{ flex: '1 1 300px' }}>
                        <Paper sx={{ p: 2, borderRadius: 3, border: `1px solid ${COLORS.border}`, height: '100%', minHeight: 350, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <Box sx={{ height: 180, width: '100%' }}>
                                <ResponsiveContainer>
                                    <RadialBarChart innerRadius="80%" outerRadius="100%" data={ghostData} startAngle={90} endAngle={-270}>
                                        <RadialBar background dataKey="value" cornerRadius={10} fill={ghostRate > 5 ? COLORS.red : COLORS.green} />
                                    </RadialBarChart>
                                </ResponsiveContainer>
                            </Box>
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography variant="h5" fontWeight={800}>Data Integrity</Typography>
                                <Typography variant="caption" color="textSecondary" fontWeight={600} display="block" sx={{ mt: 1 }}>
                                    Ghost Drivers: {ghostRate}%
                                </Typography>
                                <Typography variant="caption" color={ghostRate > 5 ? COLORS.red : COLORS.green} fontWeight={800} sx={{ mt: 1, display: 'block' }}>
                                    {ghostRate > 5 ? "PURGE REQUIRED" : "OPTIMAL"}
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>
                </Box>

                {/* ROW 5: LISTS */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
                    <Box sx={{ flex: '1 1 400px' }}>
                        {/* Zone Pressure List - Handles its own scroll */}
                        <Paper sx={{ height: 400, borderRadius: 3, border: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.border}`, bgcolor: COLORS.bg, display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle1" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Psychology sx={{ color: COLORS.indigo }} /> Zone Pressure Index
                                </Typography>
                                <Chip label={`${vitals?.database?.geofencing?.count || 0} ZONES`} size="small" sx={{ fontWeight: 800, bgcolor: COLORS.surface }} />
                            </Box>
                            <Box sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
                                {vitals?.marketplace?.cityPressure.map((city) => (
                                    <Box key={city.cityId} sx={{ p: 2, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ minWidth: 100 }}>
                                            <Typography variant="body2" fontWeight={800}>{city.cityId.toUpperCase()}</Typography>
                                            <Typography variant="caption" color="textSecondary">Wait: {city.maxWaitTime}s</Typography>
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <LinearProgress variant="determinate" value={Math.min(parseFloat(city.pressure) * 33, 100)} sx={{ height: 6, borderRadius: 3, bgcolor: '#E2E8F0', '& .MuiLinearProgress-bar': { bgcolor: parseFloat(city.pressure) > 1.2 ? COLORS.red : COLORS.indigo } }} />
                                        </Box>
                                        <Typography variant="body2" fontWeight={800}>{formatCompactNumber(city.demand)}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: '1 1 400px' }}>
                        <Paper sx={{ height: 400, p: 3, borderRadius: 3, border: `1px solid ${COLORS.border}`, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Typography variant="subtitle1" fontWeight={800}>Processing Queues</Typography>
                            <Stack spacing={4} sx={{ flex: 1, overflowY: 'auto' }}>
                                <WorkerRow label="RIDE MATCHING" data={vitals?.workers?.rideQueue} color={COLORS.indigo} icon={<Bolt fontSize="small" />} />
                                <WorkerRow label="DATA PRUNING" data={vitals?.workers?.cleanupQueue} color={COLORS.slate} icon={<Opacity fontSize="small" />} />
                                <WorkerRow label="BILLING" data={vitals?.workers?.subscriptionQueue} color={COLORS.teal} icon={<CheckCircle fontSize="small" />} />
                            </Stack>
                        </Paper>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// --- PURE COMPONENTS ---
const MiniMetric = ({ label, value, icon, color = COLORS.slate }: any) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.slate, fontSize: '0.7rem' }}>{label}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
            {icon && <Box sx={{ color: color, display: 'flex' }}>{icon}</Box>}
            <Typography variant="body2" fontWeight={800} sx={{ color: color, fontSize: '1rem' }}>{value}</Typography>
        </Stack>
    </Box>
);

const KpiCard = ({ title, value, subValue, trend, color, icon, isInverse, isLive, tooltip }: any) => {
    const isNegativeTrend = trend.startsWith('-');
    const content = (
        <Paper sx={{
            p: 2.5, borderRadius: 3, border: `1px solid ${COLORS.border}`, boxShadow: 'none',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden',
            minHeight: 140
        }}>
            {/* Background Icon */}
            <Box sx={{ position: 'absolute', right: -15, top: -15, opacity: 0.05, transform: 'scale(5)', color: color }}>{icon}</Box>

            {/* Header Row: Icon Left | Light+Chip Right */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ p: 1, borderRadius: 2, bgcolor: `${color}15`, color: color, display: 'inline-flex' }}>{icon}</Box>

                <Stack direction="row" spacing={1} alignItems="center">
                    <StatusLight sx={{ fontSize: 14, color: isLive ? COLORS.green : COLORS.red }} />
                    <Chip size="small" label={trend} sx={{
                        bgcolor: isInverse ? (trend === "STABLE" || trend === "NORMAL" ? `${COLORS.green}15` : `${COLORS.red}15`) : (isNegativeTrend ? `${COLORS.red}15` : `${COLORS.green}15`),
                        color: isInverse ? (trend === "STABLE" || trend === "NORMAL" ? COLORS.green : COLORS.red) : (isNegativeTrend ? COLORS.red : COLORS.green),
                        fontWeight: 800
                    }}
                    />
                </Stack>
            </Box>

            <Box>
                {/* Added Title Rendering here */}
                <Typography variant="body2" fontWeight={700} color="textSecondary" sx={{ mb: 0.5 }}>{title}</Typography>
                <Typography variant="h3" fontWeight={900} sx={{ color: COLORS.header, letterSpacing: '-1px' }}>
                    {typeof value === 'number' ? formatCompactNumber(value) : value}
                </Typography>
                <Typography variant="body2" fontWeight={600} color={COLORS.slate} sx={{ mt: 0.5 }}>{subValue}</Typography>
            </Box>
        </Paper>
    );

    return tooltip ? <MuiTooltip title={tooltip} arrow placement="top">{content}</MuiTooltip> : content;
}

const WorkerRow = ({ label, data, color, icon }: any) => {
    const total = (data?.active || 0) + (data?.waiting || 0);
    const progress = total > 0 ? (data.active / total) * 100 : 0;
    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ color: color, display: 'flex' }}>{icon}</Box>
                    <Typography variant="caption" fontWeight={800} color="textSecondary">{label}</Typography>
                </Stack>
                <Typography variant="caption" fontWeight={800}>
                    {formatCompactNumber(data?.active || 0)} Active / {formatCompactNumber(data?.waiting || 0)} Queued
                </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4, bgcolor: `${color}20`, '& .MuiLinearProgress-bar': { bgcolor: color } }} />
        </Box>
    );
};

const LoadingScreen = () => (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: COLORS.bg }}>
        <CircularProgress thickness={3} size={60} sx={{ color: COLORS.indigo, mb: 3 }} />
        <Typography variant="h6" fontWeight={800} color={COLORS.header}>ESTABLISHING UPLINK...</Typography>
        <Typography variant="caption" fontWeight={600} color={COLORS.slate}>SYNCING TELEMETRY SHARDS</Typography>
    </Box>
);

export default Home;